import React from "react";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "../../../context/ThemeContext";

export const FeaturedEvent = ({ content, label = "Featured Event" }) => {
	const theme = useTheme();
	const styles = useStyles();
	const eventDate = new Date(content.date);
	const formattedDate = eventDate.toLocaleDateString("en-US", {
		weekday: "long",
		month: "long",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});

	return (
		<View style={styles.container}>
			<View style={styles.header}>
				<Text style={styles.label}>{label}</Text>
			</View>
			<View style={styles.content}>
				<View style={styles.dateBox}>
					<Text style={styles.month}>{eventDate.toLocaleString("en-US", { month: "short" })}</Text>
					<Text style={styles.day}>{eventDate.getDate()}</Text>
				</View>
				<View style={styles.details}>
					<Text style={styles.title}>{content.title}</Text>
					<View style={styles.infoRow}>
						<Ionicons name="time-outline" size={16} color={theme.colors.textSecondary} />
						<Text style={styles.info}>{formattedDate}</Text>
					</View>
					<View style={styles.infoRow}>
						<Ionicons name="location-outline" size={16} color={theme.colors.textSecondary} />
						<Text style={styles.info}>{content.location}</Text>
					</View>
					<Text style={styles.description} numberOfLines={2}>
						{content.description}
					</Text>
					<View style={styles.footer}>
						<View style={styles.attendees}>
							<View style={styles.avatarStack}>
								<View style={[styles.avatar, { backgroundColor: theme.colors.systemRed }]} />
								<View style={[styles.avatar, { backgroundColor: theme.colors.systemBlue }]} />
								<View style={[styles.avatar, { backgroundColor: theme.colors.systemGreen }]} />
							</View>
							<Text style={styles.attendeesText}>12 attending</Text>
						</View>
						<TouchableOpacity style={styles.actionButton}>
							<Text style={styles.actionButtonText}>{content.actionLabel}</Text>
						</TouchableOpacity>
					</View>
				</View>
			</View>
		</View>
	);
};

const useStyles = () => {
	const theme = useTheme();

	return StyleSheet.create({
		container: {
			backgroundColor: theme.colors.systemBackground.primary,
			marginBottom: theme.spacing.sm,
			borderRadius: theme.borderRadius.lg,
			...theme.shadows.sm,
		},
		header: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
			paddingHorizontal: theme.spacing.md,
			paddingTop: theme.spacing.sm,
			paddingBottom: theme.spacing.xs,
		},
		label: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
			fontWeight: "500",
		},
		content: {
			flexDirection: "row",
			paddingHorizontal: theme.spacing.md,
			paddingBottom: theme.spacing.sm,
		},
		dateBox: {
			width: 50,
			height: 50,
			backgroundColor: theme.colors.primary,
			borderRadius: theme.borderRadius.md,
			justifyContent: "center",
			alignItems: "center",
			marginRight: theme.spacing.sm,
		},
		month: {
			color: theme.colors.systemBackground.primary,
			...theme.typography.caption2,
			fontWeight: "500",
			textTransform: "uppercase",
		},
		day: {
			color: theme.colors.systemBackground.primary,
			...theme.typography.headline,
			fontWeight: "600",
		},
		details: {
			flex: 1,
		},
		title: {
			...theme.typography.headline,
			color: theme.colors.text,
			marginBottom: theme.spacing.xs,
		},
		infoRow: {
			flexDirection: "row",
			alignItems: "center",
			gap: theme.spacing.xs,
			marginBottom: theme.spacing.xxs,
		},
		info: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
		},
		description: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
			marginBottom: theme.spacing.sm,
		},
		footer: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
		},
		attendees: {
			flexDirection: "row",
			alignItems: "center",
			gap: theme.spacing.xs,
		},
		avatarStack: {
			flexDirection: "row",
		},
		avatar: {
			width: 22,
			height: 22,
			borderRadius: 11,
			borderWidth: 2,
			borderColor: theme.colors.systemBackground.primary,
			marginLeft: -8,
		},
		attendeesText: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
		},
		actionButton: {
			backgroundColor: theme.colors.primary,
			paddingVertical: theme.spacing.xs,
			paddingHorizontal: theme.spacing.sm,
			borderRadius: theme.borderRadius.md,
		},
		actionButtonText: {
			...theme.typography.subhead,
			color: theme.colors.systemBackground.primary,
			fontWeight: "500",
		},
	});
};
